import { useState, type ElementType, type MouseEvent } from 'react';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { HelpOutline as HelpIcon } from '@mui/icons-material';
import SmartDisplayOutlinedIcon from '@mui/icons-material/SmartDisplayOutlined';
import { Avatar, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { ProfileInterface } from 'ts-interfaces';
import { BarIconButton } from '../buttons/ButtonStyles';
import { getContactForm } from '../libs/util';

interface HelpNavItem {
  icon: ElementType;
  label: string;
  link: string;
  secondary: string;
}

const getHelpNavItems = (profile?: ProfileInterface | null): HelpNavItem[] => [
  {
    icon: SmartDisplayOutlinedIcon,
    label: 'Tutorials',
    link: 'https://www.youtube.com/playlist?list=PL8nH4c8pjbq6eN-B4NwcAP9pMCdnM--i9',
    secondary: 'Watch full tutorials on YouTube',
  },
  {
    icon: GroupsOutlinedIcon,
    label: 'Join Office Hours',
    link: 'https://calendly.com/mattoboard-meetup/mattoboard-tutorial',
    secondary: 'Get live help from our team',
  },
  {
    icon: MailOutlineIcon,
    label: 'Contact Us / Feedback',
    link: getContactForm(profile),
    secondary: 'Send us a message',
  },
];

const StyledMenu = styled(Menu)(() => ({
  '& .MuiPaper-root': {
    width: '320px',
    maxWidth: 'calc(100vw - 24px)',
    maxHeight: 'calc(100vh - 96px)',
    marginTop: '8px',
    overflowX: 'hidden',
    overflowY: 'auto',
    borderRadius: '16px',
    border: '1px solid rgba(0, 0, 0, 0.06)',
    boxShadow: '0 24px 48px rgba(0, 0, 0, 0.18), 0 4px 12px rgba(0, 0, 0, 0.08)',
  },
  '& .MuiMenu-list': {
    padding: '8px',
    backgroundColor: '#ffffff',
  },
}));

export const HelpButton = ({ profile }: { profile?: ProfileInterface | null }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
    if (window.gtag && !open) window.gtag('event', 'open_help_menu');
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleLink = (link: string) => {
    if (window.gtag) window.gtag('event', 'help_menu_link', { event_category: 'help_menu', event_label: link });
    window.open(link, '_blank', 'noopener,noreferrer')?.focus();
    handleClose();
  };

  return (
    <>
      <BarIconButton aria-label="Help" style={{ marginLeft: '0px', padding: '8px' }} onClick={handleMenuOpen} size="large">
        <HelpIcon style={{ color: 'black', fontSize: '30px' }} />
      </BarIconButton>
      <StyledMenu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {getHelpNavItems(profile).map((item) => (
          <MenuItem
            key={item.link}
            onClick={() => handleLink(item.link)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              padding: '10px 16px',
              borderRadius: '12px',
              whiteSpace: 'normal',
              '&:hover': { backgroundColor: '#f7f7f7' },
            }}
          >
            <Avatar
              sx={{
                backgroundColor: '#f1f1f3',
                color: '#71717A',
                width: 48,
                height: 48,
                flexShrink: 0,
                '& .MuiSvgIcon-root': { fontSize: '20px' },
              }}
            >
              <item.icon />
            </Avatar>
            <Stack spacing={0.25} sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1a1a1a' }}>
                {item.label}
              </Typography>
              <Typography sx={{ fontSize: '12px', color: '#888', overflowWrap: 'break-word' }}>
                {item.secondary}
              </Typography>
            </Stack>
          </MenuItem>
        ))}
      </StyledMenu>
    </>
  );
};
